import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { RootState, useAppDispatch } from "../../../redux/store";
import { useSelector } from "react-redux";
import {
    convertDecimalHoursToHM,
    convertMinutesToHoursAndMinutes,
    convertToDateTimeLocalFormat,
    convertToFullDateTime,
    formatDateTime12hour,
} from "../../../utils/employeeTimeCardhelper";
import {
    BriefcaseBusiness,
    Calendar,
    ChevronRight,
    ClipboardMinus,
    Clock5,
    History,
    Loader,
    MapPin,
    NotebookPen,
    ReceiptText,
    Tag,
} from "lucide-react";
import { adjustTimeCardApi, getEmployeeTimeCardRecordDetail } from "../../../redux/features/EmployeeTimeCard";
import { getDepartments, getJobCodes, getSites } from "../../../redux/features/common";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import { AdjustTimeCardPayload } from "../../../types/state/timecards";
import Select from "react-select";
import PullToRefresh from "pulltorefreshjs";
import { deleteTimesheet } from "../../../redux/features/timesheet";
import { isPrivilege, UserPrivileges } from "../../../rules";

interface AttendanceTag {
    field: string;
    value: string;
    state?: string;
}

interface LocationState {
    employeeId?: string;
    recordData?: any;
    mode?: string;
    recordId?: string;
}

const TimecardRecordDetail: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const locationState = useLocation().state as LocationState;
    const { timecardRecordDayDetailsData } = useSelector((state: RootState) => state.employeeTimeCardData);
    const departmentOptions = useSelector((state: RootState) => state.common.departments);
    const siteOptions = useSelector((state: RootState) => state.common.sites);
    const user = useSelector((state: RootState) => state?.user?.user);
    const [isEditing, setIsEditing] = useState(false);
    const [startDateTime, setStartDateTime] = useState<string>("");
    const [endDateTime, setEndDateTime] = useState<string>("");
    const [department, setDepartment] = useState<string>("");
    const [jobCode, setJobCode] = useState<string>("");
    const [site, setSite] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const jobCodeOptions = useSelector((state: RootState) => state.common.jobCodes);
    const jobCodeSelectOptions = jobCodeOptions.map((job: any) => ({
        value: job.work_code_number,
        label: `${job.work_code_number} - ${job.work_code_name}`,
    }));

    const isAdmin = isPrivilege(user?.employee_access_role_privileges, UserPrivileges.EMPLOYEE_PRIV_ADMIN);
    const isAllowedToEdit = isPrivilege(
        user?.employee_access_role_privileges,
        UserPrivileges.EMPLOYEE_PRIV_UPDATE_TIMESHEET
    );
    const isManagerPrivilege = isPrivilege(
        user?.employee_access_role_privileges,
        UserPrivileges.USER_PRIV_UPDATE_REPORTING_EMP_TIMESHEETS
    );
    const isManager = user?.is_manager;

    const selfPrivilege = isAdmin || isAllowedToEdit;
    const managerPrivilege = isAdmin || isManagerPrivilege;

    const canEdit = isManager ? managerPrivilege : selfPrivilege;

    useEffect(() => {
        if (timecardRecordDayDetailsData) {
            setStartDateTime(convertToDateTimeLocalFormat(timecardRecordDayDetailsData?.os_emp_start_datetime));
            setEndDateTime(convertToDateTimeLocalFormat(timecardRecordDayDetailsData?.os_emp_end_datetime));
            setDepartment(timecardRecordDayDetailsData?.department?.code || "");
            setSite(timecardRecordDayDetailsData?.site?.code || "");
        }
    }, [timecardRecordDayDetailsData]);

    const fetchFilterOptions = async () => {
        setLoading(true); // Set loading to true when the API call starts
        await Promise.all([dispatch(getSites()), dispatch(getDepartments()), dispatch(getJobCodes())]);
        setLoading(false); // Set loading to false after the API call finishes
    };

    useEffect(() => {
        fetchFilterOptions();
        dispatch(setTopNavNameTitle("Timesheet Record"));
        // Pass employee_id if we're viewing someone else's record (e.g. as a manager)
        dispatch(
            getEmployeeTimeCardRecordDetail({
                id: Number(params?.id),
                employee_id: locationState?.employeeId ? Number(locationState?.employeeId) : undefined,
            })
        );
    }, []);
    // Edit function now redirects to AddTimesheetEntry component via onClick in the Edit button

    // const handleEditSave = () => {
    //   setIsEditing(!isEditing);
    //   setLoading(true);
    //   const payload: AdjustTimeCardPayload = {
    //     record_id: params.id!,
    //     employee_id: user?.id,
    //     start_datetime: convertToFullDateTime(startDateTime),
    //     end_datetime: convertToFullDateTime(endDateTime),
    //     department_code: department,
    //     site_code: site,
    //   };
    //   dispatch(adjustTimeCardApi(payload))
    //     .then((res) => {
    //       dispatch(getEmployeeTimeCardRecordDetail(Number(params?.id)));
    //       setLoading(false);
    //     })
    //     .catch((error) => {
    //       console.error("Error adjusting time card", error);
    //       setLoading(false);
    //     });
    // };
    const handleCancel = () => {
        setIsEditing(!isEditing);
        setStartDateTime(convertToDateTimeLocalFormat(timecardRecordDayDetailsData?.os_emp_start_datetime || ""));
        setEndDateTime(convertToDateTimeLocalFormat(timecardRecordDayDetailsData?.os_emp_end_datetime || ""));
        setDepartment(timecardRecordDayDetailsData?.department?.code || ""); // Reset department
        setSite(timecardRecordDayDetailsData?.site?.code || ""); // Reset site
    };

    const handleDelete = () => {
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        setDeleteLoading(true); // Start loading
        try {
            await dispatch(deleteTimesheet({ record_id: params.id! })).unwrap();
            setShowDeleteModal(false);
            navigate(-1);
        } catch (error) {
            console.error("Error deleting timecard", error);
            alert("Failed to delete timecard. Please try again.");
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
    };
    const departmentSelectOptions = departmentOptions.map((dept: any) => ({
        value: dept.department_code,
        label: `${dept.department_code} - ${dept.department_name}`,
    }));

    const siteSelectOptions = siteOptions.map((site: any) => ({
        value: site.us_site_code,
        label: `${site.us_site_code} - ${site.user_site_name}`,
    }));

    // Function for refreshing data
    const refreshData = useCallback(async () => {
        setLoading(true);
        try {
            await dispatch(
                getEmployeeTimeCardRecordDetail({
                    id: Number(params?.id),
                    employee_id: locationState?.employeeId ? Number(locationState?.employeeId) : undefined,
                })
            );
        } catch (error) {
            console.error("Error refreshing data:", error);
        } finally {
            setLoading(false);
        }
    }, [dispatch, params?.id]);

    useEffect(() => {
        PullToRefresh.init({
            mainElement: "#refresh-container",
            onRefresh: refreshData, // Call refreshData on pull-to-refresh
            instructionsPullToRefresh: "Pull to refresh",
            instructionsReleaseToRefresh: "Release to refresh",
            getMarkup: () => `
        <div class="w-full h-16 flex items-center justify-center bg-white shadow-sm">
          <div class="w-full flex items-center justify-center">
            <div class="flex items-center justify-center gap-2 text-base text-gray-600">
              <div class="h-4 w-4 border-2 border-t-transparent border-gray-500 rounded-full animate-spin"></div>
              <span>Refreshing...</span>
            </div>
          </div>
        </div>
      `,
            distThreshold: 40,
            distMax: 60,
            distReload: 50,
            shouldPullToRefresh() {
                const container = document.getElementById("refresh-container");
                return container?.scrollTop === 0;
            },
        });

        // Cleanup PullToRefresh
        return () => {
            PullToRefresh.destroyAll();
        };
    }, [refreshData]);
    // Handler for back button
    const handleBackClick = () => {
        // Check if we came from the history or directly use window history
        navigate(-1);
    };

    return (
        <>
            <div id="refresh-container" className="h-full overflow-y-auto bg-white">
                <div className="container mx-auto bg-white ">
                    {/* Back Button */}
                    {/* <div className="flex items-center mb-2 px-4 pt-3">
            <button 
              onClick={handleBackClick}
              className="flex items-center text-blue-500"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="18" 
                height="18" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                className="lucide lucide-arrow-left"
              >
                <path d="m12 19-7-7 7-7"></path>
                <path d="M19 12H5"></path>
              </svg>
              <span className="ml-1">Back</span>
            </button>
          </div> */}

                    {loading ? (
                        <div className="flex justify-center items-center py-4">
                            <Loader
                                size={32}
                                color="#2664eb"
                                strokeWidth={2.5}
                                absoluteStrokeWidth
                                className="animate-spin"
                            />
                        </div>
                    ) : (
                        <>
                            {/* <div className="grid grid-cols-6 gap-x-4 gap-y-2 px-4 border-b-2">
                <div className="col-span-6">
                  <div className="flex justify-end items-center border-gray-300 pb-2">
                    {isEditing && (
                      <button
                        className="rounded-md bg-gray-400 px-3.5 py-2  text-base font-semibold text-white shadow-xs hover:bg-gray-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 mr-4"
                        onClick={handleCancel}
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      className="rounded-md bg-blue-600 px-3.5 py-2  text-base font-semibold text-white shadow-xs hover:bg-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                      onClick={() => navigate('/m-add-timesheet-entry', { 
                        state: { 
                          recordData: timecardRecordDayDetailsData,
                          mode: 'edit',
                          recordId: params.id,
                          isManager: locationState?.employeeId ? true : false,
                          employeeId: locationState?.employeeId
                        } 
                      })}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </div> */}
                            <div className="grid grid-cols-6 gap-x-4 gap-y-2 px-4 border-b-2 pt-2">
                                <div className="col-span-6 ">
                                    <div className="flex justify-between items-start border-gray-300 pb-3">
                                        <div className="flex flex-col justify-between w-1/2 pr-2">
                                            <div className="flex items-center space-x-2">
                                                <span className="text-gray-500">
                                                    <Calendar size={16} color="#2563eb" strokeWidth={1.5} />
                                                </span>
                                                <span className="font-normal text-sm text-gray-800">
                                                    Start Date & Time
                                                </span>
                                            </div>
                                            {isEditing ? (
                                                <input
                                                    type="datetime-local"
                                                    value={startDateTime}
                                                    onChange={(e) => {
                                                        setStartDateTime(e.target.value);
                                                    }}
                                                    className="mt-1 w-full rounded-md border border-gray-300 p-1 text-xs text-gray-800 focus:border-blue-500 focus:ring focus:ring-blue-200"
                                                />
                                            ) : (
                                                <span className="text-sm text-gray-800 mt-1">
                                                    {/* {formatDateTime(startDateTime)} */}
                                                    {formatDateTime12hour(
                                                        timecardRecordDayDetailsData?.os_emp_start_datetime || ""
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-col justify-between pl-2">
                                            <div className="flex items-center space-x-2">
                                                <span className="text-gray-500">
                                                    <Calendar size={16} color="#2563eb" strokeWidth={1.5} />
                                                </span>
                                                <span className="font-normal text-sm text-gray-800">
                                                    End Date & Time
                                                </span>
                                            </div>
                                            {isEditing ? (
                                                <input
                                                    type="datetime-local"
                                                    value={endDateTime}
                                                    onChange={(e) => setEndDateTime(e.target.value)}
                                                    className="mt-1 w-full rounded-md border border-gray-300 p-1 text-xs text-gray-800 focus:border-blue-500 focus:ring focus:ring-blue-200"
                                                />
                                            ) : (
                                                <span className="text-sm text-gray-800 mt-1">
                                                    {/* {formatDateTime(endDateTime)} */}
                                                    {formatDateTime12hour(
                                                        timecardRecordDayDetailsData?.os_emp_end_datetime || ""
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-flow-col grid-rows-2 gap-x-4 gap-y-0 border-b-2 px-4">
                                <div className="col-span-2 pt-2">
                                    <div className="flex justify-between items-center">
                                        <div className="space-x-2 flex">
                                            <Clock5 size={16} color="#2563eb" strokeWidth={1.5} />
                                            <span className="font-normal text-sm text-gray-800">Total Hours</span>
                                        </div>
                                        <div className="font-normal text-sm text-gray-800">
                                            {convertDecimalHoursToHM(Number(timecardRecordDayDetailsData?.hours) || 0)}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-span-2 pb-2">
                                    <div className="flex justify-between items-center">
                                        <div className="space-x-2">
                                            <i className="fas fa-clock text-gray-500 invisible"></i>
                                            <span className="font-normal text-sm text-gray-800">Regular</span>
                                        </div>
                                        <div className="font-normal text-sm text-gray-800">
                                            {convertDecimalHoursToHM(
                                                Number(timecardRecordDayDetailsData?.regular_hours) || 0
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="row-span-2 border-l-2 pl-3 pt-2">
                                    <div className="flex flex-col">
                                        <span className="font-normal text-sm text-gray-800">Overtime</span>
                                        <span className="font-normal text-sm text-gray-800">
                                            {" "}
                                            {convertDecimalHoursToHM(
                                                Number(timecardRecordDayDetailsData?.overtime_hours) || 0
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-6 gap-x-4 gap-y-2 px-4 py-3 border-b-2">
                                <div className="col-span-6">
                                    <div className="flex justify-between items-center">
                                        <div className="space-x-2 flex">
                                            <ClipboardMinus size={16} color="#2563eb" strokeWidth={1.5} />
                                            <span className="font-normal text-sm text-gray-800">Deductions</span>
                                        </div>
                                        <div className="font-normal text-sm text-gray-800">
                                            {" "}
                                            {convertMinutesToHoursAndMinutes(
                                                Number(timecardRecordDayDetailsData?.deduction || 0)
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-6 gap-x-4 gap-y-2 px-4 py-3 border-b-2">
                                <div className="col-span-6">
                                    <div className="flex justify-between items-center">
                                        <div className="space-x-2 flex">
                                            <ReceiptText size={16} color="#2563eb" strokeWidth={1.5} />
                                            <span className="font-normal text-sm text-gray-800">Code</span>
                                        </div>
                                        <div className="font-normal text-sm text-gray-800">
                                            <span className="text-sm text-gray-800 mt-1">
                                                {isEditing ? (
                                                    <Select
                                                        value={jobCodeSelectOptions.find(
                                                            (option) => option.value === jobCode
                                                        )}
                                                        onChange={(selectedOption: any) =>
                                                            setJobCode(selectedOption.value)
                                                        }
                                                        options={jobCodeSelectOptions}
                                                        className="mt-1 w-full rounded-md p-1 text-sm text-gray-800"
                                                    />
                                                ) : (
                                                    <span className="text-sm text-gray-800 mt-1">
                                                        {timecardRecordDayDetailsData?.work_code?.name}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-6 gap-x-4 gap-y-2 px-4 py-3 border-b-2">
                                <div className="col-span-6">
                                    <div className="flex justify-between items-center">
                                        <div className="space-x-2 flex">
                                            <BriefcaseBusiness size={16} color="#2563eb" strokeWidth={1.5} />
                                            <span className="font-normal text-sm text-gray-800">Department</span>
                                        </div>
                                        <div className="font-normal text-sm text-gray-800">
                                            {isEditing ? (
                                                <Select
                                                    value={departmentSelectOptions.find(
                                                        (option) => option.value === department
                                                    )}
                                                    onChange={(selectedOption: any) =>
                                                        setDepartment(selectedOption.value)
                                                    }
                                                    options={departmentSelectOptions}
                                                    className="mt-1 w-full rounded-md p-1 text-sm text-gray-800"
                                                />
                                            ) : (
                                                <span className="text-sm text-gray-800 mt-1">
                                                    {timecardRecordDayDetailsData?.department?.name}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-6 gap-x-4 gap-y-2 px-4 py-3 border-b-2">
                                <div className="col-span-6">
                                    <div className="flex justify-between items-center">
                                        <div className="space-x-2 flex">
                                            <MapPin size={16} color="#2563eb" strokeWidth={1.5} />
                                            <span className="font-normal text-sm text-gray-800">Site</span>
                                        </div>
                                        <div className="font-normal text-sm text-gray-800">
                                            {isEditing ? (
                                                <Select
                                                    value={siteSelectOptions.find((option) => option.value === site)}
                                                    onChange={(selectedOption: any) => setSite(selectedOption?.value)}
                                                    options={siteSelectOptions}
                                                    className="mt-1 w-full rounded-md p-1 text-sm text-gray-800"
                                                />
                                            ) : (
                                                <span className="text-sm text-gray-800 mt-1">
                                                    {timecardRecordDayDetailsData?.site?.name}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-6 gap-x-4 gap-y-2 px-4 py-3 border-b-2">
                                <div className="col-span-6">
                                    <div className="flex justify-between items-center">
                                        <div className="space-x-2 flex">
                                            <Clock5 size={16} color="#2563eb" strokeWidth={1.5} />
                                            <span className="font-normal text-sm text-gray-800">Break</span>
                                        </div>
                                        <div className="font-normal text-sm text-gray-800">
                                            {timecardRecordDayDetailsData &&
                                                Array.isArray(timecardRecordDayDetailsData?.break) &&
                                                timecardRecordDayDetailsData?.break?.length > 0 &&
                                                timecardRecordDayDetailsData.break.join(", ")}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-6 gap-x-4 gap-y-2 px-4 py-3 border-b-2">
                                <div className="col-span-6">
                                    <div className="flex justify-between items-center">
                                        <div className="space-x-2 flex">
                                            <ReceiptText size={16} color="#2563eb" strokeWidth={1.5} />
                                            <span className="font-normal text-sm text-gray-800">Shift</span>
                                        </div>
                                        <div className="font-normal text-sm text-gray-800">
                                            {timecardRecordDayDetailsData?.shift?.shift_start_time}
                                            {" - "}
                                            {timecardRecordDayDetailsData?.shift?.shift_end_time}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-6 gap-x-4 gap-y-2 px-4 py-3 border-b-2">
                                <div className="col-span-6">
                                    <div
                                        className="flex justify-between items-center cursor-pointer"
                                        onClick={() => navigate(`/m-attendance-tags/${params?.id}`)}
                                    >
                                        <div className="space-x-2 flex">
                                            <Tag size={16} color="#2563eb" strokeWidth={1.5} />
                                            <span className="font-normal text-sm text-gray-800">Attendance Tags</span>
                                        </div>
                                        <div className="text-blue-600 text-sm flex items-end">
                                            <span>{timecardRecordDayDetailsData?.attendence_tags?.length || 0}</span>
                                            <ChevronRight size={16} color="#2563eb" strokeWidth={1.5} />
                                        </div>

                                        {/* {(() => {
                      if (!timecardRecordDayDetailsData || !Array.isArray(timecardRecordDayDetailsData.attendence_tags)) {
                        return <div className="text-sm text-gray-800">-</div>;
                      }

                      try {
                        const tags = timecardRecordDayDetailsData.attendence_tags;

                        const clockInTags = tags.filter(t => t?.state === 'TC_STATUS_CLOCKIN');
                        const clockOutTags = tags.filter(t => t?.state === 'TC_STATUS_CLOCKOUT');

                        const formatTags = (tagList: typeof tags) =>
                          tagList.length > 0
                            ? tagList
                                .map(t => {
                                  const name = t?.field_name ?? '';
                                  const value = t?.value ?? '';
                                  return name ? `${name}${value ? ` (${value})` : ''}` : '';
                                })
                                .filter(Boolean)
                                .join(', ')
                            : '-';

                        return (
                          <div className="text-sm text-gray-800">
                           {clockInTags.length>0 && <div className="flex flex-col"><strong>Clock In:</strong> {formatTags(clockInTags)}</div>}
                          { clockOutTags.length>0 && <div className="flex flex-col"><strong>Clock Out:</strong> {formatTags(clockOutTags)}</div>}
                          </div>
                        );
                      } catch (err) {
                        console.error("Error rendering attendance tags:", err);
                        return <div className="text-sm text-gray-800">-</div>;
                      }
                    })()} */}
                                        {!timecardRecordDayDetailsData?.attendence_tags?.length && (
                                            <div className="text-sm text-gray-800">-</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-6 gap-x-4 gap-y-2 px-4 py-3 border-b-2">
                                <div className="col-span-6">
                                    <div
                                        className="flex justify-between items-center"
                                        onClick={() => navigate(`/m-notesList/${params?.id}`)}
                                    >
                                        <div className="space-x-2 flex">
                                            <NotebookPen size={16} color="#2563eb" strokeWidth={1.5} />
                                            <span className="font-normal text-sm text-gray-800">Notes</span>
                                        </div>
                                        <div className="text-blue-600 text-sm flex items-end">
                                            <span>{timecardRecordDayDetailsData?.notes_count || 0}</span>
                                            <ChevronRight size={16} color="#2563eb" strokeWidth={1.5} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-6 gap-x-4 gap-y-2 px-4 py-3 border-b-2">
                                <div className="col-span-6">
                                    <div
                                        className="flex justify-between items-center"
                                        onClick={() => navigate(`/m-changeHistory/${params?.id}`)}
                                    >
                                        <div className="space-x-2 flex">
                                            <History size={16} color="#2563eb" strokeWidth={1.5} />
                                            <span className="font-normal text-sm text-gray-800">Audit History</span>
                                        </div>
                                        <div className="flex items-end text-blue-600 text-sm">
                                            <span>{timecardRecordDayDetailsData?.audit_count || 0}</span>
                                            <ChevronRight size={16} color="#2563eb" strokeWidth={1.5} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* <div className="grid grid-cols-6 gap-x-4 gap-y-2 px-4 py-2 ">
        <div className="col-span-6 ">
          <button
            className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium text-sm"
            onClick={() => alert("Submit for Approval clicked")}
          >
            Submit for Approval
          </button>
        </div>
      </div> */}

                            {/* Bottom action buttons row */}
                            {canEdit && (
                                <div className="grid grid-cols-2 gap-x-4 gap-y-2 px-4 py-4 mt-4 sticky bottom-0 bg-white shadow-md">
                                    <div className="col-span-1">
                                        <button
                                            className="w-full rounded-md bg-transparent border border-red-600 px-3.5 py-2 text-base text-red-600 hover:bg-red-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                                            onClick={handleDelete}
                                            disabled={deleteLoading}
                                        >
                                            {deleteLoading ? (
                                                <div className="flex items-center justify-center">
                                                    <Loader
                                                      size={16}
                                                        color="#dc2626"
                                                        strokeWidth={2}
                                                        absoluteStrokeWidth
                                                        className="animate-spin mr-2"
                                                    />
                                                    Deleting...
                                                </div>
                                            ) : (
                                                "Delete"
                                            )}
                                        </button>
                                    </div>
                                    <div className="col-span-1">
                                        <button
                                            className="w-full rounded-md bg-blue-600 px-3.5 py-2 text-base font-semibold text-white shadow-xs hover:bg-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                                            onClick={() =>
                                                navigate("/m-add-timesheet-entry", {
                                                    state: {
                                                        recordData: timecardRecordDayDetailsData,
                                                        mode: "edit",
                                                        recordId: params.id,
                                                        isManager: locationState?.employeeId ? true : false,
                                                        employeeId: locationState?.employeeId,
                                                    },
                                                })
                                            }
                                        >
                                            Edit
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Delete Confirmation Modal */}
                            {showDeleteModal && (
                                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                                    <div className="bg-white w-5/6 max-w-md rounded-lg p-6 shadow-lg">
                                        <div className="flex justify-center items-center mb-4 text-red-600">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-12 w-12"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                />
                                            </svg>
                                        </div>
                                        <h3 className="text-xl font-bold text-center text-gray-800 mb-4">
                                            Delete Timecard Entry
                                        </h3>
                                        <p className="text-gray-600 text-center mb-6">
                                            Are you sure you want to delete this timecard entry? This action cannot be
                                            undone.
                                        </p>
                                        <div className="flex justify-center space-x-4">
                                            <button
                                                onClick={handleDeleteCancel}
                                                className="px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-md hover:bg-gray-300 transition-colors"
                                                disabled={deleteLoading}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleDeleteConfirm}
                                                disabled={deleteLoading}
                                                className="px-4 py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700 transition-colors flex items-center justify-center"
                                            >
                                                {deleteLoading ? (
                                                    <>
                                                        <Loader
                                                            size={16}
                                                            color="#ffffff"
                                                            strokeWidth={2}
                                                            absoluteStrokeWidth
                                                            className="animate-spin mr-2"
                                                        />
                                                        Deleting...
                                                    </>
                                                ) : (
                                                    "Delete"
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default TimecardRecordDetail;
